import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountsState } from '../store/store'
import { useLocation } from 'react-router'
import { Account } from '../store/store'
import { RequestState } from '../../types'
import './Accounts.css'
import Content from '../../../components/layout/Content'
import ContentHeader from '../../../components/layout/ContentHeader'
import HeaderTitle from '../../../components/layout/HeaderTitle'
import AccountsRow from './AccountsRow'
import AccountsRowCompact from './AccountsRowCompact'

type Props = {
  state: AccountsState
  partnerId: string
  loadAccountsSearchResults: (partnerId: string, search: string, includeDeactivated: boolean, page: number) => Promise<void>
  loadAccountFilters: (accountId: string) => Promise<void>
}

function Accounts(props: Props) {
  const { state, partnerId, loadAccountsSearchResults, loadAccountFilters } = props
  const { loadedPages, totalPages, totalCount, requestState, accounts } = state
  const { t } = useTranslation(['accounts', 'global'])
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const search = params.get('search')
  const includeDeactivated = params.get('deactivated') === "true" || false

  useEffect(
    () => {
      if (search) {
        loadAccountsSearchResults(partnerId, search, includeDeactivated, 1)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partnerId, search, includeDeactivated]

  )

  useEffect(
    () => {
      if (loadedPages > 0) {
        accounts.forEach((account: Account) => {
          if (account.active) {
            loadAccountFilters(account.id)
          }
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partnerId, search, includeDeactivated, loadedPages]

  )

  function loadNextPage() {
    if (partnerId) {
      if (search) {
        loadAccountsSearchResults(partnerId, search, includeDeactivated, loadedPages + 1)
      }
    }
  }

  function renderLoadMore() {
    if (totalPages && loadedPages > 0 && loadedPages < totalPages && requestState !== RequestState.Error) {
      return (
        <div className="flex-center">
          <button id="load-more" disabled={requestState === RequestState.Loading} onClick={loadNextPage}>
            {requestState !== RequestState.Loading ? t('global:load_more') : t('global:loading')}
          </button>
        </div>
      )
    }
  }

  return (
    <>
      <ContentHeader>
        <HeaderTitle>
          {t('searched_for')}&nbsp;
          <b>{search}</b> {totalCount > 0 && <span>&nbsp;({totalCount})</span>}
        </HeaderTitle>
      </ContentHeader>
      <Content>
        {requestState === RequestState.Error && (
          <div className="padding-m">Unable to load accounts.</div>
        )}

        {requestState === RequestState.Loading && (
          <div className="panel-loading">
            <i className="fas fa-spinner fa-spin font-size-xl"></i>
            <span>{t('global:loading')}</span>
          </div>
        )}

        {requestState === RequestState.Loaded && accounts.length === 0 && (
          <div id="no-subscriptions">{t('no_accounts')}</div>
        )}

        {requestState !== RequestState.Error && accounts && accounts.length > 0 && (
          <table id="accounts-table" className="content-table" cellSpacing={0} cellPadding={5}>
            <thead>
              <tr>
                <th>{t('fields.name')}</th>
                <th>{t('fields.subscription')}</th>
                <th>{t('fields.last_activity')}</th>
                <th>{t('fields.records')}</th>
                <th>{t('filtersWarn')}</th>
                <th>{t('filtersAlert')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {accounts.map((account: Account) => (
                <AccountsRow key={account.id} account={account} partnerId={partnerId} />
              ))}
            </tbody>
          </table>
        )}

        <div id="responsive-accounts-container">
          {accounts.map((account: Account) => (
            <AccountsRowCompact key={`${account.id}-compact`} account={account} partnerId={partnerId} />
          ))}
        </div>

        {renderLoadMore()}
      </Content>
    </>
  )
}

export default Accounts
